import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Checkbox, CheckCircle, Clock, Modal, TextInput } from "@opendate-app/setlist";
import TurnstileChallenge from "../../../shared/TurnstileChallenge";
import TurnstileDisclosure from "../../../shared/TurnstileDisclosure";
import { validateEmail } from "../../../shared/validationUtils";
import useFocusOnChange from "../../ClassesShared/useFocusOnChange";

const FORM_ID = "pc-waitlist-form";
const ACKNOWLEDGE_ERROR_ID = "pc-waitlist-acknowledge-error";
const TEXT_ALERTS_HINT_ID = "pc-waitlist-text-alerts-hint";

const formErrors = (form) => {
  const errors = {};
  if (!form.full_name.trim()) errors.full_name = "Enter your name.";
  if (!form.email.trim()) errors.email = "Enter your email address.";
  else if (!validateEmail(form.email.trim())) errors.email = "Enter an email address like name@example.com.";
  if (!form.acknowledged_claim_window) errors.acknowledged_claim_window = "Check this box to join the waitlist.";
  return errors;
};

const blankForm = { full_name: "", email: "", phone: "", text_alerts_opt_in: false, acknowledged_claim_window: false };

export const joinedMessage = (peopleAhead, texting = false) => {
  const notice = texting ? "We'll email and text you as soon as a spot opens." : "We'll email you as soon as a spot opens.";
  if (!peopleAhead) return `You're next in line. ${notice}`;
  return `${peopleAhead} ${peopleAhead === 1 ? "person" : "people"} ahead of you. ${notice}`;
};

export default function ClassWaitlistJoin({ classTitle, joinUrl, claimWindowLabel, textAlertsAvailable = false, turnstileSiteKey, fetchImpl = fetch }) {
  const [open, setOpen] = useState(() => typeof window !== "undefined" && window.location.hash === "#waitlist");
  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState([]);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [focusRequest, setFocusRequest] = useState(0);
  const turnstileRef = useRef(null);
  const formRef = useRef(null);
  const resultRef = useFocusOnChange(Boolean(result));

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updatePhone = (value) => setForm((current) => ({ ...current, phone: value, text_alerts_opt_in: value.trim() === "" ? false : current.text_alerts_opt_in }));
  const fieldErrors = formErrors(form);
  const fieldError = (field) => (showFieldErrors ? fieldErrors[field] : undefined);
  const hasPhone = form.phone.trim() !== "";
  const texting = textAlertsAvailable && hasPhone && form.text_alerts_opt_in;

  const verify = async () => {
    if (!turnstileSiteKey || !turnstileRef.current) return null;
    try {
      return await turnstileRef.current.executeAsync();
    } catch (error) {
      turnstileRef.current.reset();
      throw error;
    }
  };

  useEffect(() => {
    if (focusRequest) formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [focusRequest]);

  const submit = async (event) => {
    event.preventDefault();
    if (working) return;
    if (Object.keys(fieldErrors).length > 0) {
      setShowFieldErrors(true);
      setFocusRequest((count) => count + 1);
      return;
    }
    setWorking(true);
    setErrors([]);
    let recaptchaToken;
    try {
      recaptchaToken = await verify();
    } catch (error) {
      setErrors(["We couldn't verify this browser. Please try again."]);
      setWorking(false);
      return;
    }
    try {
      const response = await fetchImpl(joinUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ class_waitlist_entry: { ...form, text_alerts_opt_in: texting }, recaptcha_token: recaptchaToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setErrors(data.errors || ["We couldn't add you to the waitlist. Please try again."]);
        return;
      }
      setResult(data);
    } catch (error) {
      setErrors(["We couldn't reach the server. Check your connection and try again."]);
    } finally {
      setWorking(false);
    }
  };

  const close = () => {
    setOpen(false);
    setErrors([]);
  };

  const footer = result ? (
    <div className="pc-modal-actions">
      <Button variant="primary" onClick={close}>Done</Button>
    </div>
  ) : (
    <div className="pc-modal-actions">
      <Button variant="secondary" onClick={close} disabled={working}>Cancel</Button>
      <Button type="submit" form={FORM_ID} variant="primary" aria-disabled={working || undefined}>
        {working ? "Joining…" : "Join Waitlist"}
      </Button>
    </div>
  );

  const description = result ? null : (
    <span>
      {"We'll email you if a spot opens up in "}
      <strong>{classTitle}</strong>
      {`. Joining is free, and if a spot opens we'll hold it for you for ${claimWindowLabel}.`}
    </span>
  );

  return (
    <div>
      <div className="pc-block-action">
        <Button variant="primary" onClick={() => setOpen(true)} data-testid="join-waitlist">Join Waitlist</Button>
      </div>
      {open && (
        <Modal open onClose={close} type={result ? "success" : "warning"} icon={result ? CheckCircle : Clock} layout="vertical" size="sm" title={result ? "You're on the List" : "Join Waitlist"} description={description} footer={footer}>
          {result && (
            <div ref={resultRef} tabIndex={-1}>
              {result.status === "joined" && (
                <div data-testid="waitlist-joined">
                  <p>{`You're on the waitlist for ${classTitle}.`}</p>
                  <p className="mb-0">{joinedMessage(result.people_ahead, result.text_alerts)}</p>
                </div>
              )}
              {result.status === "already_on_list" && (
                <div data-testid="waitlist-already-on-list">
                  <p className="mb-0">{`This email is already on the waitlist for ${classTitle}. Check your inbox for the link to your waitlist spot.`}</p>
                </div>
              )}
            </div>
          )}
          {!result && (
            <form id={FORM_ID} ref={formRef} onSubmit={submit} noValidate>
              {errors.length > 0 && <div className="mb-3"><Alert color="error" role="alert" description={errors.join(" ")} /></div>}
              <div className="pc-modal-fields">
                <TextInput label="Full name" required autoComplete="name" maxLength={255} placeholder="Your name" value={form.full_name} error={fieldError("full_name")} onChange={(e) => update("full_name", e.target.value)} />
                <TextInput label="Email" required type="email" autoComplete="email" maxLength={255} placeholder="you@example.com" hint="We'll only email you about this waitlist." value={form.email} error={fieldError("email")} onChange={(e) => update("email", e.target.value)} />
                <TextInput label="Phone (optional)" type="tel" autoComplete="tel" maxLength={50} placeholder="(555) 555-0123" value={form.phone} onChange={(e) => updatePhone(e.target.value)} />
              </div>
              {textAlertsAvailable && (
                <div data-testid="waitlist-text-alerts">
                  <Checkbox
                    size="sm"
                    label="Text me when a spot opens"
                    checked={texting}
                    disabled={!hasPhone}
                    aria-describedby={TEXT_ALERTS_HINT_ID}
                    onChange={(e) => update("text_alerts_opt_in", e.target.checked)}
                  />
                  <p className="pc-muted small mt-1 mb-3" id={TEXT_ALERTS_HINT_ID}>
                    {hasPhone ? "If a spot is offered to you, we'll text your claim link and deadline. Msg & data rates may apply. Reply STOP to opt out." : "Add a phone number above to get a text."}
                  </p>
                </div>
              )}
              <Checkbox
                size="sm"
                label={`I understand I'll have ${claimWindowLabel} to complete checkout if a spot opens, or it goes to the next person and I move to the back of the line.`}
                checked={form.acknowledged_claim_window}
                aria-required="true"
                aria-invalid={fieldError("acknowledged_claim_window") ? "true" : undefined}
                aria-describedby={fieldError("acknowledged_claim_window") ? ACKNOWLEDGE_ERROR_ID : undefined}
                onChange={(e) => update("acknowledged_claim_window", e.target.checked)}
              />
              {fieldError("acknowledged_claim_window") && <p className="pc-text-error small mt-1 mb-0" id={ACKNOWLEDGE_ERROR_ID}>{fieldError("acknowledged_claim_window")}</p>}
              {turnstileSiteKey && (
                <div>
                  <TurnstileChallenge ref={turnstileRef} sitekey={turnstileSiteKey} />
                  <TurnstileDisclosure />
                </div>
              )}
            </form>
          )}
        </Modal>
      )}
    </div>
  );
}

ClassWaitlistJoin.propTypes = {
  classTitle: PropTypes.string.isRequired,
  joinUrl: PropTypes.string.isRequired,
  claimWindowLabel: PropTypes.string.isRequired,
  textAlertsAvailable: PropTypes.bool,
  turnstileSiteKey: PropTypes.string,
  fetchImpl: PropTypes.func,
};
